import type {
  SedrAuditEvent,
  SedrDashboardMetrics,
  SedrPhasesList,
  SedrRequirement,
} from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { renderPhasesStrip } from './sedrPhases';
import {
  formatSedrAuditDocument,
  formatSedrDateTime,
  renderMetricsRow,
  statusBadgeClass,
  tSedrAuditAction,
  tSedrStatus,
  type SedrMetricKind,
} from './sedrUi';

import { escapeHtml } from './sedrEscape';

export const SEDR_DASHBOARD_LIST_LIMIT = 8;

export type CompliancePhaseId = string | null | 'unassigned';

export function completionPercent(metrics: SedrDashboardMetrics): number {
  if (metrics.totalRequirements <= 0) return 0;
  return Math.min(100, Math.round((metrics.approved / metrics.totalRequirements) * 100));
}

export function pickAttentionRequirements(
  items: SedrRequirement[],
  canApprove: boolean,
  limit = SEDR_DASHBOARD_LIST_LIMIT,
): { items: SedrRequirement[]; total: number } {
  if (canApprove) {
    const underReview = items.filter((r) => r.status === 'UnderReview');
    const lifecyclePending = items.filter(
      (r) => Boolean(r.requestedLifecycleStatus) && r.status !== 'UnderReview',
    );
    const ordered = [...underReview, ...lifecyclePending];
    return { items: ordered.slice(0, limit), total: ordered.length };
  }

  const pending = items.filter((r) => r.status === 'PendingUpload' || r.status === 'Denied');
  return { items: pending.slice(0, limit), total: pending.length };
}

export function takeNewestAuditEvents(
  events: SedrAuditEvent[],
  limit = SEDR_DASHBOARD_LIST_LIMIT,
): SedrAuditEvent[] {
  return [...events]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, limit);
}

export function filterRequirementsByPhase(
  items: SedrRequirement[],
  phaseId: CompliancePhaseId,
): SedrRequirement[] {
  if (phaseId === null) return items;
  if (phaseId === 'unassigned') {
    return items.filter((r) => !r.phaseId);
  }
  return items.filter((r) => r.phaseId === phaseId);
}

function truncateText(value: string | null | undefined, max = 90): string {
  const trimmed = (value ?? '').trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1)}…`;
}

function renderCompletionStrip(metrics: SedrDashboardMetrics): HTMLElement {
  const block = document.createElement('div');
  block.className = 'sedr-dashboard__progress sedr-dashboard__progress--embedded';
  const percent = completionPercent(metrics);

  if (metrics.totalRequirements === 0) {
    block.innerHTML = `
      <p class="sedr-dashboard__empty" data-i18n="sedr.dashboard.emptyRoom"></p>
    `;
    applyI18n(block);
    return block;
  }

  block.innerHTML = `
    <div class="sedr-dashboard__progress-meta">
      <span class="sedr-dashboard__progress-title" data-i18n="sedr.dashboard.completionTitle"></span>
      <span class="sedr-dashboard__progress-summary">${escapeHtml(t('sedr.dashboard.completionSummary', {
        approved: metrics.approved,
        total: metrics.totalRequirements,
        percent,
      }))}</span>
    </div>
    <div class="sedr-dashboard__progress-bar" role="progressbar" aria-valuenow="${percent}" aria-valuemin="0" aria-valuemax="100">
      <div class="sedr-dashboard__progress-fill" style="width: ${percent}%"></div>
    </div>
  `;
  applyI18n(block);
  return block;
}

function renderAttentionQueue(options: {
  requirements: SedrRequirement[];
  canApprove: boolean;
  onOpenRequirement: (requirementId: string) => void;
  onViewAll: () => void;
}): HTMLElement {
  const section = document.createElement('section');
  section.className = 'sedr-dashboard__section sedr-attention';
  const { items, total } = pickAttentionRequirements(options.requirements, options.canApprove);

  section.innerHTML = `
    <div class="sedr-dashboard__section-header">
      <h3 data-i18n="sedr.dashboard.attentionTitle"></h3>
      ${total > SEDR_DASHBOARD_LIST_LIMIT
        ? `<button type="button" class="button ghost sedr-dashboard__view-all" data-i18n="sedr.dashboard.viewAll"></button>`
        : '<span class="sedr-dashboard__header-spacer" aria-hidden="true"></span>'}
    </div>
    <div class="sedr-attention-body"></div>
  `;

  const body = section.querySelector('.sedr-attention-body') as HTMLElement;
  if (items.length === 0) {
    body.innerHTML = `<p class="sedr-dashboard__empty" data-i18n="sedr.dashboard.attentionEmpty"></p>`;
  } else {
    const list = document.createElement('ul');
    list.className = 'sedr-attention-list';
    for (const req of items) {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sedr-attention-row';
      btn.innerHTML = `
        <div class="sedr-attention-row__top">
          <span class="sedr-attention-row__code">${escapeHtml(req.requirementCode)}</span>
          <span class="${statusBadgeClass(req.status)}">${escapeHtml(tSedrStatus(req.status))}</span>
        </div>
        <div class="sedr-attention-row__desc">${escapeHtml(truncateText(req.title ?? req.description ?? ''))}</div>
        ${req.phaseName
          ? `<div class="sedr-attention-row__phase">${escapeHtml(req.phaseName)}</div>`
          : ''}
      `;
      btn.addEventListener('click', () => options.onOpenRequirement(req.id));
      li.append(btn);
      list.append(li);
    }
    body.append(list);
  }

  section.querySelector('.sedr-dashboard__view-all')?.addEventListener('click', options.onViewAll);
  applyI18n(section);
  return section;
}

function renderRecentActivity(options: {
  events: SedrAuditEvent[];
  onViewAll: () => void;
}): HTMLElement {
  const section = document.createElement('section');
  section.className = 'sedr-dashboard__section sedr-activity';
  const newest = takeNewestAuditEvents(options.events);

  section.innerHTML = `
    <div class="sedr-dashboard__section-header">
      <h3 data-i18n="sedr.dashboard.activityTitle"></h3>
      <button type="button" class="button ghost sedr-dashboard__view-all" data-i18n="sedr.dashboard.viewAll"></button>
    </div>
    <div class="sedr-activity-body"></div>
  `;

  const body = section.querySelector('.sedr-activity-body') as HTMLElement;
  if (newest.length === 0) {
    body.innerHTML = `<p class="sedr-dashboard__empty" data-i18n="sedr.dashboard.activityEmpty"></p>`;
  } else {
    const list = document.createElement('ul');
    list.className = 'sedr-activity-list';
    for (const evt of newest) {
      const li = document.createElement('li');
      li.className = 'sedr-activity-row';
      const documentLabel = formatSedrAuditDocument(evt.detailsJson);
      const actor = evt.actorDisplay?.trim() || '—';
      const meta = documentLabel === '—'
        ? actor
        : `${actor} · ${documentLabel}`;
      li.innerHTML = `
        <span class="sedr-activity-row__time">${escapeHtml(formatSedrDateTime(evt.createdAt))}</span>
        <span class="sedr-activity-row__action">${escapeHtml(tSedrAuditAction(evt.action))}</span>
        <span class="sedr-activity-row__meta">${escapeHtml(truncateText(meta, 110))}</span>
      `;
      list.append(li);
    }
    body.append(list);
  }

  section.querySelector('.sedr-dashboard__view-all')?.addEventListener('click', options.onViewAll);
  applyI18n(section);
  return section;
}

export function renderSedrDashboard(options: {
  metrics: SedrDashboardMetrics;
  requirements: SedrRequirement[];
  phasesList: SedrPhasesList;
  auditEvents: SedrAuditEvent[];
  canApprove: boolean;
  canManagePhases: boolean;
  onSelectMetric: (metric: SedrMetricKind) => void;
  onOpenRequirement: (requirementId: string) => void;
  onViewAttentionAll: () => void;
  onSelectPhase: (phaseId: string | null) => void;
  onViewAudit: () => void;
  onManagePhases?: () => void;
}): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'sedr-tab-panel sedr-tab-panel--dashboard';

  panel.append(renderMetricsRow(options.metrics, { onSelect: options.onSelectMetric }));

  const grid = document.createElement('div');
  grid.className = 'sedr-dashboard__grid';

  grid.append(renderAttentionQueue({
    requirements: options.requirements,
    canApprove: options.canApprove,
    onOpenRequirement: options.onOpenRequirement,
    onViewAll: options.onViewAttentionAll,
  }));

  const phasesHost = document.createElement('section');
  phasesHost.className = 'sedr-dashboard__section sedr-dashboard__section--phases';
  phasesHost.innerHTML = `
    <div class="sedr-dashboard__section-header">
      <h3 data-i18n="sedr.phases.title"></h3>
      ${options.canManagePhases
        ? `<button type="button" class="button ghost sedr-dashboard__view-all" id="sedr-phases-manage" data-i18n="sedr.phases.manage"></button>`
        : '<span class="sedr-dashboard__header-spacer" aria-hidden="true"></span>'}
    </div>
  `;
  phasesHost.append(renderCompletionStrip(options.metrics));
  const phases = renderPhasesStrip({
    list: options.phasesList,
    canWrite: false,
    onSelectPhase: options.onSelectPhase,
    omitHeader: true,
  });
  phases.classList.add('sedr-phases--dashboard');
  phasesHost.append(phases);
  phasesHost.querySelector('#sedr-phases-manage')?.addEventListener('click', () => options.onManagePhases?.());
  grid.append(phasesHost);

  panel.append(grid);
  panel.append(renderRecentActivity({
    events: options.auditEvents,
    onViewAll: options.onViewAudit,
  }));

  applyI18n(panel);
  return panel;
}
